// ============================================================
// THE OBJECT'S PLATE AND MARK, as geometry (2026-10-26, boards 5 and 7 of the Cover & Objects canvas).
//
// One diamond - an octahedron seen a little from the side, resting on its ring - in four states read off the health
// ramp: INTACT, DAMAGED (cracks on its faces), CRITICAL (more of them) and DESTROYED (shards on the ground, the ring
// dashed). The ring is a gauge that drains with its Wounds. Every crack and shard is a fixed table: nothing random,
// so every screen draws the same break (and no stray Math.random shifts the seeded dice, gotcha 5).
//
// Pure. Zero React dependency. The plate (PlateObject) and the display's mark both read it.
// ============================================================
import { healthBand, vitalsOf } from '../healthRamp';
import type { Target } from '../../types';
import type { Vec3 } from '../battlefield/types';

export type ObjectState = 'intact' | 'damaged' | 'critical' | 'destroyed';

/** Destroyed once it carries K (its only critical) or has gone past 0 Wounds; else by its health band. */
export function objectState(target: Pick<Target, 'conditions' | 'wounds' | 'horde'>): ObjectState {
  if ((target.conditions ?? []).includes('K')) return 'destroyed';
  const { shown, total } = vitalsOf(target as Target);
  if (shown < 0) return 'destroyed';
  const band = healthBand(shown, total);
  if (band === 'low' || band === 'crit') return 'critical';
  return band === 'med' ? 'damaged' : 'intact';
}

export interface Diamond {
  /** Visible faces, back to front, as SVG points: the two upper and the two lower. */
  faces: { id: 'upL' | 'upR' | 'lowL' | 'lowR'; points: string }[];
  /** The equator, drawn faint over the faces (board 7's glyph). */
  equator: [number, number, number, number];
}

const pt = (x: number, y: number) => `${round(x)},${round(y)}`;
const round = (n: number) => Math.round(n * 10) / 10;

/**
 * The diamond centred on (cx, cy), `w` wide and `h` tall. The front vertex sits a little right of centre, which is
 * what turns a rhombus into a solid: the left faces are broader than the right.
 */
export function diamond(cx: number, cy: number, w: number, h: number): Diamond {
  const [top, bottom, left, right] = [cy - h / 2, cy + h / 2, cx - w / 2, cx + w / 2];
  const front = cx + w * 0.12;
  return {
    faces: [
      { id: 'upL', points: [pt(cx, top), pt(left, cy), pt(front, cy)].join(' ') },
      { id: 'upR', points: [pt(cx, top), pt(front, cy), pt(right, cy)].join(' ') },
      { id: 'lowL', points: [pt(left, cy), pt(front, cy), pt(cx, bottom)].join(' ') },
      { id: 'lowR', points: [pt(front, cy), pt(right, cy), pt(cx, bottom)].join(' ') },
    ],
    equator: [left, cy, right, cy],
  };
}

/** Cracks in unit space (-0.5..0.5 across, -0.5..0.5 down), most visible first. */
const CRACKS: readonly (readonly [number, number])[][] = [
  [
    [-0.08, -0.3],
    [-0.14, -0.12],
    [-0.06, -0.02],
  ],
  [
    [0.1, 0.06],
    [0.16, 0.2],
    [0.08, 0.3],
  ],
  [
    [-0.24, 0.02],
    [-0.16, 0.12],
    [-0.2, 0.2],
  ],
  [
    [0.04, -0.36],
    [0.14, -0.18],
    [0.22, -0.08],
  ],
];
const CRACK_COUNT: Record<ObjectState, number> = { intact: 0, damaged: 2, critical: 4, destroyed: 0 };

/** The cracks its state shows, as SVG polyline points, fitted to the diamond's box. */
export function cracksFor(state: ObjectState, cx: number, cy: number, w: number, h: number): string[] {
  return CRACKS.slice(0, CRACK_COUNT[state]).map((line) => line.map(([x, y]) => pt(cx + x * w, cy + y * h)).join(' '));
}

/** The shards a destroyed object leaves on the ground: small triangles round where it stood, unit space. */
const SHARDS: readonly (readonly [number, number])[][] = [
  [
    [-0.42, 0],
    [-0.3, -0.16],
    [-0.22, 0.02],
  ],
  [
    [-0.12, 0.04],
    [-0.02, -0.22],
    [0.08, 0.02],
  ],
  [
    [0.18, 0],
    [0.3, -0.12],
    [0.38, 0.04],
  ],
  [
    [-0.06, 0.12],
    [0.04, 0.02],
    [0.12, 0.14],
  ],
];

/** The shards round (cx, groundY), `w` across. */
export function shardsAt(cx: number, groundY: number, w: number): string[] {
  return SHARDS.map((s) => s.map(([x, y]) => pt(cx + x * w, groundY + y * w * 0.5)).join(' '));
}

/**
 * The gauge on an elliptical ring: the arc for `fill` percent, from the front of the ring round to the left and back
 * (clockwise on screen), as an SVG path; empty at 0, the whole ring at 100.
 */
export function gaugeArc(cx: number, cy: number, rx: number, ry: number, fill: number): string {
  const f = Math.min(100, Math.max(0, fill)) / 100;
  if (f <= 0) return '';
  // From the bottom of the ellipse (its front), clockwise on screen: SVG's y runs down, so +angle turns clockwise.
  const at = (t: number) => pt(cx + rx * Math.sin(-t), cy + ry * Math.cos(t));
  if (f >= 1)
    return `M${at(0)} A${round(rx)} ${round(ry)} 0 1 1 ${at(Math.PI)} A${round(rx)} ${round(ry)} 0 1 1 ${at(0)}`;
  const end = f * 2 * Math.PI;
  return `M${at(0)} A${round(rx)} ${round(ry)} 0 ${f > 0.5 ? 1 : 0} 1 ${at(end)}`;
}

/**
 * The four ground corners of a terrain Object's box (w along its heading, d across), at its feet: the outline its
 * gauge runs round (board 6), in the same turn the box's solid takes (0 looks along +x, 90 along +z).
 */
export function boxFootprint(
  token: { x: number; z: number; elevation: number; heading?: number },
  box: { w: number; d: number },
): Vec3[] {
  const h = ((token.heading ?? 0) * Math.PI) / 180;
  const [c, s] = [Math.cos(h), Math.sin(h)];
  const local: [number, number][] = [
    [box.w / 2, box.d / 2],
    [-box.w / 2, box.d / 2],
    [-box.w / 2, -box.d / 2],
    [box.w / 2, -box.d / 2],
  ];
  return local.map(([lx, lz]) => ({ x: token.x + lx * c - lz * s, y: token.elevation, z: token.z + lx * s + lz * c }));
}

type Pt = { x: number; y: number };
const dist = (a: Pt, b: Pt) => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * The gauge round a closed outline: the points of the share `fill` (0-100) of its perimeter, from the first corner
 * round in order - empty at 0, the whole closed outline at 100. The terrain box's ring, on the screen.
 */
export function outlineGauge(outline: readonly Pt[], fill: number): Pt[] {
  const f = Math.min(100, Math.max(0, fill)) / 100;
  if (f <= 0 || outline.length < 2) return [];
  const ring = [...outline, outline[0]];
  const total = ring.slice(1).reduce((sum, p, i) => sum + dist(ring[i], p), 0);
  let left = f * total;
  const out: Pt[] = [ring[0]];
  for (let i = 1; i < ring.length && left > 1e-9; i++) {
    const leg = dist(ring[i - 1], ring[i]);
    const t = Math.min(1, left / leg);
    out.push(
      t >= 1
        ? ring[i]
        : { x: ring[i - 1].x + (ring[i].x - ring[i - 1].x) * t, y: ring[i - 1].y + (ring[i].y - ring[i - 1].y) * t },
    );
    left -= leg;
  }
  return out;
}
